'use client';

import { useEffect } from 'react';
import LogRocket from 'logrocket';
import { useTracking } from './TrackingProvider';
import { redactPrivateUrl } from '@/lib/telemetry-privacy';

const LOGROCKET_APP_ID = process.env.NEXT_PUBLIC_LOGROCKET_APP_ID;
const IS_LOCALHOST = typeof window !== 'undefined' && ['localhost', '127.0.0.1', '[::1]'].includes(window.location.hostname);

/**
 * Initializes LogRocket session replay and error tracking.
 * Must be rendered inside TrackingProvider to access deviceId.
 * Disabled on localhost to avoid polluting production analytics.
 */
export function LogRocketProvider({ children }: { children: React.ReactNode }) {
  const { deviceId } = useTracking();

  useEffect(() => {
    if (!LOGROCKET_APP_ID || IS_LOCALHOST) {
      return;
    }

    // Initialize LogRocket
    LogRocket.init(LOGROCKET_APP_ID, {
      release: process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA,
      browser: { urlSanitizer: redactPrivateUrl },
      dom: { privateClassNameBlocklist: ['study-private'] },
      console: {
        isEnabled: true,
      },
      network: {
        // Don't log request/response bodies (privacy)
        requestSanitizer: (request) => {
          if (request.url.includes('/api/account')) return null;
          // Redact auth headers
          if (request.headers['authorization']) {
            request.headers['authorization'] = '[REDACTED]';
          }
          return request;
        },
        responseSanitizer: response => response.url?.includes('/api/account') ? null : response,
      },
    });

    console.log('[LogRocket] Initialized');
  }, []);

  // Identify user when deviceId is available
  useEffect(() => {
    if (!LOGROCKET_APP_ID || IS_LOCALHOST || !deviceId) return;

    LogRocket.identify(deviceId, {
      // Add any traits you want to track
      // Don't include PII here
    });

    console.log(`[LogRocket] Identified device ${deviceId.slice(0, 8)}...`);
  }, [deviceId]);

  return <>{children}</>;
}
